import React, { useEffect, useState } from "react";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Form, Button, Row, Col } from "react-bootstrap";
import {
  useGetBenchLocationsMutation,
  useGetCourtNumbersMutation,
  useSubmitCauseListMutation,
} from "../../../../redux/services/external/apiExternal";
import CauseListModal from "./CauseListModal";
import { useParams } from "react-router-dom";

const CauseList = () => {
  const { benchId } = useParams();

  const [getBenchLocations, { data: benches, error, isLoading }] =
    useGetBenchLocationsMutation();
  const [
    getCourtNumbers,
    { data: courtNumbers, isLoading: isCourtLoading, error: courtError },
  ] = useGetCourtNumbersMutation();
  const [
    submitCauseList,
    { data: responseCauseList, isLoading: isSubmitting, error: submitError },
  ] = useSubmitCauseListMutation();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [resetFormFunction, setResetFormFunction] = useState(null);

  const initialValues = {
    bench: "",
    order: "",
    caseDate: "",
  };

  useEffect(() => {
    getBenchLocations();
  }, [getBenchLocations]);

  useEffect(() => {
    getCourtNumbers({
      bench_location: benchId,
    });
  }, []);

  const validationSchema = Yup.object({
    bench: Yup.string().required("Bench is required"),
    order: Yup.string().required("Order type is required"),
    caseDate: Yup.string().required("Date is required"),
  });

  const [errorString, setErrorString] = useState("");

  const handleSubmit = async (values, { resetForm }) => {
    setResetFormFunction(() => resetForm);

    const payload = {
      listing_date: values.caseDate,
      court_no: values.order,
      bench_id: values.bench,
    };

    try {
      const result = await submitCauseList(payload).unwrap();

      if (result.error !== "") {
        setErrorString(result.error);
        setModalIsOpen(true); // Open modal if there's an error
      } else {
        setModalIsOpen(true);
        resetForm();
      }
    } catch (err) {
      console.error("Error submitting cause list:", err);
    }
  };

  const handleReset = () => {
    resetFormFunction(); // Call resetForm if it exists
    setErrorString(""); // Clear any error messages
  };

  const handleBenchChange = async (e, handleChange) => {
    handleChange(e); // Update the form state with the selected bench

    const selectedBenchId = parseInt(e.target.value);

    if (selectedBenchId) {
      try {
        const response = await getCourtNumbers({
          bench_location: selectedBenchId,
        });
      } catch (err) {
        console.error("Error fetching court numbers:", err);
      }
    }
  };

  const handleModalClose = () => {
    setModalIsOpen(false);
    resetFormFunction(); // Call resetForm if it exists
    setErrorString(""); // Clear any error messages
  };

  return (
    <>
      <div className="about-content mt-4">
        {/* <h2>Cause List (RCT)</h2> */}
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            resetForm,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="bench" className="form-group">
                    <Form.Label>RCT Bench</Form.Label>
                    <Form.Control
                      as="select"
                      name="bench"
                      value={values.bench} // Set the selected bench
                      onChange={(e) => {
                        setFieldValue("order", "");
                        setFieldTouched("order", false);
                        handleBenchChange(e, handleChange);
                      }}
                      onBlur={handleBlur}
                    >
                      <option hidden>Select Bench</option>
                      {isLoading && <option>Loading benches...</option>}
                      {error && <option>Error loading benches</option>}
                      {benches?.data?.length > 0 ? (
                        benches?.data?.map((bench) => (
                          <option key={bench.id} value={bench.id}>
                            {bench.railway_bench_name}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="">No Benches available</option>
                        </>
                      )}
                    </Form.Control>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="order" className="form-group">
                    <Form.Label>Select Court</Form.Label>
                    <Form.Control
                      as="select"
                      name="order"
                      value={values.order}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option hidden>Select Court</option>
                      {isCourtLoading && (
                        <option>Loading court numbers...</option>
                      )}
                      {courtError && (
                        <option>Error loading court numbers</option>
                      )}
                      {courtNumbers?.data?.length > 0 ? (
                        courtNumbers.data.map((court) => (
                          <option key={court.court_no} value={court.court_no}>
                            {court.court_description}
                          </option>
                        ))
                      ) : (
                        <option value="">No courts available</option>
                      )}
                    </Form.Control>
                    <ErrorMessage
                      name="order"
                      component="div"
                      style={{ color: "red", fontSize: "0.8rem" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="caseDate" className="form-group">
                    <Form.Label>Select Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="caseDate"
                      value={values.caseDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage
                      name="caseDate"
                      component="div"
                      style={{ color: "red", fontSize: "0.8rem" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}></Col>

                <Col md={12} className="text-center mt-4">
                  <Button type="submit" className="btn yellow-btn mx-2">
                    Submit
                  </Button>
                  <Button
                    className="grey-btn btn btn-secondary"
                    onClick={resetForm}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
      <CauseListModal
        responseCauseList={responseCauseList}
        submitError={submitError}
        errorString={errorString}
        show={modalIsOpen}
        handleClose={handleModalClose}
      />
    </>
  );
};

export default CauseList;
